import { supabase } from '../lib/supabase';

/**
 * Real-time subscription service for syncing data across devices
 * Handles subscriptions for transactions, budgets, and receipts
 */
export const realtimeService = {
  /**
   * Subscribe to real-time changes for transactions table
   * @param {Function} onInsert - Callback when new transaction is inserted
   * @param {Function} onUpdate - Callback when transaction is updated
   * @param {Function} onDelete - Callback when transaction is deleted
   * @returns {Object} Subscription channel
   */
  subscribeToTransactions(onInsert, onUpdate, onDelete) {
    const channel = supabase?.channel('transactions-realtime')?.on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'transactions' },
        (payload) => {
          if (onInsert) onInsert(payload?.new);
        }
      )?.on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'transactions' },
        (payload) => {
          if (onUpdate) onUpdate(payload?.new);
        }
      )?.on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'transactions' },
        (payload) => {
          if (onDelete) onDelete(payload?.old);
        }
      )?.subscribe();

    return channel;
  },

  /**
   * Subscribe to real-time changes for budget_limits table
   * @param {Function} onInsert - Callback when new budget is inserted
   * @param {Function} onUpdate - Callback when budget is updated
   * @param {Function} onDelete - Callback when budget is deleted
   * @returns {Object} Subscription channel
   */
  subscribeToBudgets(onInsert, onUpdate, onDelete) {
    const channel = supabase?.channel('budgets-realtime')?.on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'budget_limits' },
        (payload) => {
          if (onInsert) onInsert(payload?.new);
        }
      )?.on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'budget_limits' },
        (payload) => {
          if (onUpdate) onUpdate(payload?.new);
        }
      )?.on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'budget_limits' },
        (payload) => {
          if (onDelete) onDelete(payload?.old);
        }
      )?.subscribe();

    return channel;
  },

  /**
   * Subscribe to real-time changes for accounts table (for receipt-related balance updates)
   * @param {Function} onInsert - Callback when new account is inserted
   * @param {Function} onUpdate - Callback when account is updated
   * @param {Function} onDelete - Callback when account is deleted
   * @returns {Object} Subscription channel
   */
  subscribeToAccounts(onInsert, onUpdate, onDelete) {
    const channel = supabase?.channel('accounts-realtime')?.on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'accounts' },
        (payload) => {
          if (onInsert) onInsert(payload?.new);
        }
      )?.on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'accounts' },
        (payload) => {
          if (onUpdate) onUpdate(payload?.new);
        }
      )?.on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'accounts' },
        (payload) => {
          if (onDelete) onDelete(payload?.old);
        }
      )?.subscribe();

    return channel;
  },

  /**
   * Subscribe to all financial data changes (transactions, budgets, accounts)
   * @param {Object} callbacks - Object containing callback functions
   * @param {Function} callbacks.onTransactionChange - Callback for transaction changes
   * @param {Function} callbacks.onBudgetChange - Callback for budget changes
   * @param {Function} callbacks.onAccountChange - Callback for account changes
   * @returns {Array} Array of subscription channels
   */
  subscribeToAllFinancialData(callbacks) {
    const channels = [];

    // Subscribe to transactions
    if (callbacks?.onTransactionChange) {
      const transactionChannel = this.subscribeToTransactions(
        callbacks?.onTransactionChange,
        callbacks?.onTransactionChange,
        callbacks?.onTransactionChange
      );
      channels?.push(transactionChannel);
    }

    // Subscribe to budgets
    if (callbacks?.onBudgetChange) {
      const budgetChannel = this.subscribeToBudgets(
        callbacks?.onBudgetChange,
        callbacks?.onBudgetChange,
        callbacks?.onBudgetChange
      );
      channels?.push(budgetChannel);
    }

    // Subscribe to accounts
    if (callbacks?.onAccountChange) {
      const accountChannel = this.subscribeToAccounts(
        callbacks?.onAccountChange,
        callbacks?.onAccountChange,
        callbacks?.onAccountChange
      );
      channels?.push(accountChannel);
    }

    return channels;
  },

  /**
   * Unsubscribe from a channel
   * @param {Object} channel - The channel to unsubscribe from
   */
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  /**
   * Unsubscribe from multiple channels
   * @param {Array} channels - Array of channels to unsubscribe from
   */
  unsubscribeAll(channels) {
    if (channels && Array.isArray(channels)) {
      channels?.forEach(channel => {
        if (channel) {
          supabase?.removeChannel(channel);
        }
      });
    }
  }
};

export default realtimeService;
